import { useRef, useState, useCallback, useEffect, type ReactNode } from 'react'
import { isNonDragTarget } from '../hooks/useDraggable'

export type SnapPoint = 'collapsed' | 'half' | 'expanded'

interface BottomSheetProps {
  /** Additional host class for narrowly scoped layout composition. */
  className?: string
  /** Current snap target — controlled from parent */
  snap: SnapPoint
  onSnapChange: (snap: SnapPoint) => void
  /** Heights for each snap point (vh or px) */
  collapsedHeight?: number   // px, default 80
  halfRatio?: number         // 0-1, default 0.5
  expandedRatio?: number     // 0-1, default 0.9
  /** Content sections */
  collapsedContent: ReactNode
  halfContent?: ReactNode
  expandedContent?: ReactNode
  /** When true, the middle 'half' snap is removed — dragging/flinging resolves
   *  only to collapsed or expanded. Used where the half state has no content
   *  to show (e.g. the route-picker). */
  disableHalf?: boolean
  /** Lay the drag grip ON TOP of the collapsed content instead of above it.
   *  The grip normally occupies its own 20px strip of sheet background — over
   *  an edge-to-edge hero video that reads as a white bar cutting off the
   *  picture (Alex, Issue #380). With this set the grip floats over the media
   *  and the content starts at the sheet's top edge. The drag gesture itself is
   *  unaffected: it is hit-tested by the sheet's top 40px, not by this element. */
  handleOverlay?: boolean
  /** Randlos-Modus (Issue #384): reduziert das Bottom-Padding des Scroll-
   *  Containers (20→10px), damit ein edge-to-edge Hero bis fast an die
   *  Unterkante läuft. Seitliche 20px bleiben (der Hero gleicht sie selbst
   *  per Negativ-Margin aus, alle anderen Inhalte brauchen sie). Immer mit
   *  `handleOverlay` kombinieren, sonst bleibt oben die Griff-Leiste. */
  contentFlush?: boolean
  /** Issue #392: entfernt die 1px-Trennkante an der Oberkante (borderTop) —
   *  fuer den Desktop-Hero-Fall, wo Bewegtbild bis an die Kante geht und der
   *  Saum wie ein Rendering-Fehler wirkt. Am Handy bleibt die Kante bewusst
   *  (Alex: sie hebt das Video von der Karte dahinter ab). */
  edgeless?: boolean
  /** Theme */
  dark?: boolean
  /** z-index */
  zIndex?: number
  /** When true, on desktop (>=900px wide) the sheet becomes a floating
   *  panel pinned bottom-left with a max-width, instead of a full-width
   *  bottom sheet. Mobile keeps the bottom-sheet behaviour. */
  floatingDesktop?: boolean
  /** When set, the sheet floats as a NARROW panel pinned bottom-left (width =
   *  `mobileFloat.width`) instead of a full-width bottom sheet — used for the
   *  portrait route-hero tile on mobile (Issue #372, Alex). */
  mobileFloat?: { width: string } | null
  /** Distance from the bottom of the viewport the sheet is anchored at — used
   *  to lift it above a fixed footer nav (CSS length or px). Default 0. */
  bottomOffset?: number | string
  /** When true, on desktop the floating panel is drag-to-reposition: grabbing
   *  the background (not the grip, a button or text) moves it freely; the
   *  offset persists under `dragStorageKey`. Snap-resize via the top grip
   *  keeps working. Mobile ignores this. */
  draggable?: boolean
  dragStorageKey?: string
  /** Sync-Abgang (Alex 27.07.): true lässt das gemountete Sheet nach unten
   *  aus dem Bild sliden (0.3s, gleiches Timing wie der Karten-Morph des
   *  ActiveRouteCardLayer), statt beim Unmount hart zu verschwinden. Der
   *  Aufrufer unmountet nach Ablauf der Animation. */
  exiting?: boolean
  /** Hide the drag affordance when the sheet is used as a fixed app shell. */
  hideHandle?: boolean
  /** Keep scrolling/buttons active but disable snap and free-move gestures. */
  interactionLocked?: boolean
  /** Absolute lets a full-screen host own the positioning context. */
  positionMode?: 'fixed' | 'absolute'
  /** Sofort im Ziel-Snap mounten statt von collapsed hochzufahren —
   *  fuer Vollflaechen-Dialoge wie das Onboarding (Alex 117281: der
   *  weisse Bereich unter dem einfahrenden Sheet soll nie sichtbar
   *  sein). POI-Sheets behalten ihre Einfahr-Animation (default). */
  startAtSnap?: boolean
}

export default function BottomSheet({
  className,
  snap,
  onSnapChange,
  collapsedHeight = 80,
  halfRatio = 0.5,
  expandedRatio = 0.9,
  collapsedContent,
  halfContent,
  expandedContent,
  disableHalf = false,
  handleOverlay = false,
  contentFlush = false,
  edgeless = false,
  dark = true,
  zIndex = 60,
  floatingDesktop = false,
  mobileFloat = null,
  bottomOffset = 0,
  draggable = false,
  dragStorageKey,
  exiting = false,
  hideHandle = false,
  interactionLocked = false,
  positionMode = 'fixed',
  startAtSnap = false,
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef({
    dragging: false,
    startY: 0,
    startHeight: 0,
    currentHeight: 0,
    velocity: 0,
    lastY: 0,
    lastTime: 0,
  })
  const [height, setHeight] = useState(() => {
    if (!startAtSnap || typeof window === 'undefined') return collapsedHeight
    // Gleiche Rechnung wie getSnapHeights — zum First-Render sind Props +
    // Viewport schon da, gemessen wird nichts.
    const vh = window.innerHeight
    const anchor = typeof bottomOffset === 'number' ? bottomOffset : 0
    const eff = disableHalf && snap === 'half' ? 'expanded' : snap
    if (eff === 'expanded') return Math.min(vh * expandedRatio, vh - anchor - 8)
    if (eff === 'half') return vh * halfRatio
    return collapsedHeight
  })
  const [isDragging, setIsDragging] = useState(false)
  const [isMoving, setIsMoving] = useState(false)
  // Auftritt symmetrisch zum Abgang: das Sheet slidet beim Mount von unten
  // ein (erst nach dem ersten Paint auf 0, sonst keine Transition).
  const [entered, setEntered] = useState(false)
  useEffect(() => { const id = requestAnimationFrame(() => setEntered(true)); return () => cancelAnimationFrame(id) }, [])

  // Free drag-to-reposition (desktop floating only). `pos` is a translate()
  // offset on top of the CSS bottom-right anchor; persisted so a moved box
  // stays put across reloads. `moveRef` tracks an in-flight reposition drag,
  // kept separate from the snap-resize drag above so the two never collide.
  const [pos, setPos] = useState<{ x: number; y: number }>(() => {
    if (dragStorageKey && typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem(dragStorageKey)
        if (raw) { const p = JSON.parse(raw); if (typeof p?.x === 'number' && typeof p?.y === 'number') return p }
      } catch { /* ignore corrupt value */ }
    }
    return { x: 0, y: 0 }
  })
  const moveRef = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 })
  useEffect(() => {
    if (!dragStorageKey || typeof window === 'undefined') return
    try { window.localStorage.setItem(dragStorageKey, JSON.stringify(pos)) } catch { /* quota / private mode */ }
  }, [pos, dragStorageKey])

  // Calculate snap heights from viewport. The sheet is anchored ABOVE
  // `bottomOffset` (footer nav) — a ratio near 1 must not push the grip out
  // of the top of the viewport, so expanded is additionally capped at the
  // space that actually exists above the anchor (minus a small top gap).
  const getSnapHeights = useCallback(() => {
    const vh = window.innerHeight
    const anchor = typeof bottomOffset === 'number' ? bottomOffset : 0
    return {
      collapsed: collapsedHeight,
      half: vh * halfRatio,
      expanded: Math.min(vh * expandedRatio, vh - anchor - 8),
    }
  }, [collapsedHeight, halfRatio, expandedRatio, bottomOffset])

  // Animate to snap when `snap` prop changes
  useEffect(() => {
    if (isDragging) return
    let alive = true
    queueMicrotask(() => {
      if (!alive) return
      const heights = getSnapHeights()
      // If the half snap is disabled but we're somehow asked for it, treat it
      // as expanded so the sheet never rests at the contentless half height.
      const eff = disableHalf && snap === 'half' ? 'expanded' : snap
      setHeight(heights[eff])
    })
    return () => { alive = false }
  }, [snap, isDragging, getSnapHeights, disableHalf])

  // Find nearest snap point
  const findNearestSnap = useCallback((h: number, velocity: number): SnapPoint => {
    const heights = getSnapHeights()
    const threshold = 60 // px — momentum threshold

    // Half snap removed: resolve only to collapsed or expanded.
    if (disableHalf) {
      if (velocity < -threshold) return 'expanded'
      if (velocity > threshold) return 'collapsed'
      const mid = (heights.collapsed + heights.expanded) / 2
      return h >= mid ? 'expanded' : 'collapsed'
    }

    // Strong upward swipe → expand to next
    if (velocity < -threshold) {
      if (h < heights.half) return 'half'
      return 'expanded'
    }
    // Strong downward swipe → collapse to previous
    if (velocity > threshold) {
      if (h > heights.half) return 'half'
      return 'collapsed'
    }

    // No strong swipe — snap to nearest
    const dists = [
      { snap: 'collapsed' as SnapPoint, d: Math.abs(h - heights.collapsed) },
      { snap: 'half' as SnapPoint, d: Math.abs(h - heights.half) },
      { snap: 'expanded' as SnapPoint, d: Math.abs(h - heights.expanded) },
    ]
    dists.sort((a, b) => a.d - b.d)
    return dists[0].snap
  }, [getSnapHeights, disableHalf])

  // Whether a background-grab should reposition the panel: only on the desktop
  // floating panel (the CSS floating layout kicks in at >=900px). Below that
  // it's a full-width bottom sheet where free-move makes no sense.
  const moveAllowed = useCallback(
    () => draggable && floatingDesktop && typeof window !== 'undefined' && window.innerWidth >= 900,
    [draggable, floatingDesktop],
  )

  // Pointer handlers. Two independent gestures share the sheet root:
  //   • top 40px grip  → snap-resize (height), existing behaviour
  //   • background grab → free reposition (translate), desktop floating only
  // Grabbing a button or text never starts either (isNonDragTarget guard).
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const rect = sheetRef.current?.getBoundingClientRect()
    if (!rect) return
    const relY = e.clientY - rect.top

    // Top grip → snap-resize.
    if (relY <= 40) {
      dragRef.current = {
        dragging: true,
        startY: e.clientY,
        startHeight: height,
        currentHeight: height,
        velocity: 0,
        lastY: e.clientY,
        lastTime: Date.now(),
      }
      setIsDragging(true)
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
      return
    }

    // Background (not grip, not a button/text) → free reposition.
    if (moveAllowed() && sheetRef.current && !isNonDragTarget(e.target, sheetRef.current)) {
      const m = moveRef.current
      m.active = true
      m.startX = e.clientX
      m.startY = e.clientY
      m.baseX = pos.x
      m.baseY = pos.y
      setIsMoving(true)
      try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* unsupported */ }
    }
  }, [height, moveAllowed, pos])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    // Free reposition takes priority — it's the only active gesture when set.
    const m = moveRef.current
    if (m.active) {
      setPos({ x: m.baseX + (e.clientX - m.startX), y: m.baseY + (e.clientY - m.startY) })
      return
    }

    const d = dragRef.current
    if (!d.dragging) return

    const dy = d.startY - e.clientY // positive = dragging up
    const newHeight = Math.max(collapsedHeight, Math.min(getSnapHeights().expanded, d.startHeight + dy))

    // Track velocity
    const now = Date.now()
    const dt = now - d.lastTime
    if (dt > 0) {
      d.velocity = (e.clientY - d.lastY) / dt * 1000 // px/sec, positive = downward
    }
    d.lastY = e.clientY
    d.lastTime = now
    d.currentHeight = newHeight

    setHeight(newHeight)
  }, [collapsedHeight, getSnapHeights])

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    const m = moveRef.current
    if (m.active) {
      m.active = false
      setIsMoving(false)
      try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* noop */ }
      return
    }

    const d = dragRef.current
    if (!d.dragging) return
    d.dragging = false
    setIsDragging(false)

    const nearest = findNearestSnap(d.currentHeight, d.velocity)
    onSnapChange(nearest)
  }, [findNearestSnap, onSnapChange])

  // Canonical overlay-glass tokens (frog-view.css :root / .fv-page--night).
  // This is the same token the Style-Guide demos use for floating UI bars
  // (header, bottom sheet, footer-nav, buttons) — defined ONCE, consumed
  // everywhere. .footer-nav reads from the same tokens so sheet + footer
  // share identical glass parameters and join seamlessly.
  const bg = 'var(--ar-overlay-bg)'
  const borderColor = 'var(--ar-overlay-border)'
  const handleColor = dark
    ? 'rgba(247, 245, 240, 0.3)'
    : 'rgba(0, 0, 0, 0.2)'

  return (
    <div
      ref={sheetRef}
      className={[
        'bs-sheet',
        floatingDesktop ? 'bs-sheet--floating-desktop' : '',
        className ?? '',
      ].filter(Boolean).join(' ')}
      /* Reposition-Offset für Mess-Konsumenten (measurePickerCard):
         transform trägt Slide-in/out UND pos-Drag — wer die SETTLED-Position
         eines Kindes braucht, zieht (matrix - pos) ab. */
      data-pos-x={pos.x}
      data-pos-y={pos.y}
      style={{
        // position:fixed (not absolute) so the sheet stacks correctly against
        // other fixed-position siblings on iOS Safari. iOS treats fixed and
        // absolute elements in separate compositing layers — mixing them
        // ignores zIndex and renders fixed elements on top regardless. Since
        // the right-column toolbar buttons in NavigatePage are
        // position:fixed, the sheet must also be fixed to compete via zIndex.
        position: positionMode,
        bottom: mobileFloat ? (typeof bottomOffset === 'number' ? bottomOffset + 12 : `calc(${bottomOffset} + 12px)`) : bottomOffset,
        left: mobileFloat ? 12 : 0,
        right: mobileFloat ? 'auto' : 0,
        width: mobileFloat ? mobileFloat.width : undefined,
        height,
        // Free-reposition offset (desktop floating); composes on top of the
        // CSS bottom-right anchor. undefined when unmoved so it never fights
        // the default layout.
        transform: `${pos.x === 0 && pos.y === 0 ? '' : `translate(${pos.x}px, ${pos.y}px) `}translateY(${exiting || !entered ? 'calc(100% + 24px)' : '0px'})`,
        zIndex,
        background: bg,
        // Canonical --ar-overlay-blur — same value the footer-nav uses,
        // so the seam between the two bars is invisible (identical
        // rgba + identical blur radius = one continuous glass surface).
        backdropFilter: 'var(--ar-overlay-blur)',
        WebkitBackdropFilter: 'var(--ar-overlay-blur)',
        borderTop: edgeless ? 'none' : `1px solid ${borderColor}`,
        borderRadius: mobileFloat ? 16 : '16px 16px 0 0',
        // Issue #475 (Alex): Beim Abgang fuhr das Sheet nur nach unten weg.
        // Weil die Routen-Karten dabei kurz uebereinanderliegen, las sich das
        // als „das andere faehrt hinten durch" statt als Verschwinden. Ein
        // gleichzeitiges Ausblenden nimmt der Bewegung genau diese Zweideutig-
        // keit: Was durchscheint, verschwindet dabei schon.
        opacity: exiting || !entered ? 0 : 1,
        transition: isDragging || isMoving
          ? 'none'
          : 'height 0.35s cubic-bezier(0.32, 0.72, 0, 1), transform 0.3s ease, opacity 0.24s ease',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        // The floating tile is a SEPARATE UX element, not a drawer (Alex): no
        // vertical pull-up, so it must not swallow touch — the inner route
        // picker owns the left/right swipe between routes.
        touchAction: mobileFloat || interactionLocked ? 'auto' : 'none',
        fontFamily: "'Space Grotesk', system-ui, sans-serif",
        color: dark ? '#F7F5F0' : '#1A1F16',
      }}
      onPointerDown={mobileFloat || interactionLocked ? undefined : onPointerDown}
      onPointerMove={mobileFloat || interactionLocked ? undefined : onPointerMove}
      onPointerUp={mobileFloat || interactionLocked ? undefined : onPointerUp}
      onPointerCancel={mobileFloat || interactionLocked ? undefined : onPointerUp}
    >
      {/* Drag handle — drawer only. The floating tile can't be pulled up, so it
          must not advertise a grip. */}
      {!mobileFloat && !hideHandle && (
        <div style={{
          // Über Vollflächen-Inhalten stapeln (Species-3D legt einen
          // fixed-Canvas mit zIndex 1 über die Sheet-Fläche — der Grip
          // muss sichtbar und greifbar bleiben).
          position: 'relative', zIndex: 4,
          display: 'flex',
          justifyContent: 'center',
          padding: '10px 0 6px',
          cursor: 'grab',
          flexShrink: 0,
          // Overlay mode: float above the content so an edge-to-edge hero video
          // reaches the sheet's top edge. Not interactive — the drag is
          // hit-tested by the sheet's top 40px (see onPointerDown).
          ...(handleOverlay
            ? { position: 'absolute' as const, top: 0, left: 0, right: 0, zIndex: 3, pointerEvents: 'none' as const }
            : {}),
        }}>
          <div style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            // Over media the theme colour would vanish — light bar with a soft
            // shadow so it stays legible on any frame.
            background: handleOverlay ? 'rgba(255,255,255,0.9)' : handleColor,
            ...(handleOverlay ? { boxShadow: '0 1px 4px rgba(0,0,0,0.45)' } : {}),
          }} />
        </div>
      )}

      {/* Scrollable content — .bs-sheet__scroll carries the Apple-style glass
          scrollbar (Issue #320, styled in navigation.css). */}
      <div className="bs-sheet__scroll" style={{
        flex: 1,
        overflowY: snap === 'collapsed' ? 'hidden' : 'auto',
        overflowX: 'hidden',
        padding: contentFlush ? '0 20px 10px' : '0 20px 20px',
        WebkitOverflowScrolling: 'touch',
      }}>
        {/* Always visible: collapsed content */}
        {collapsedContent}

        {/* Half content: POI detail or route summary.
            Opacity fades in ONLY while actively dragging (a smooth reveal as you
            pull the sheet up). At any RESTING snap it's fully opaque — otherwise
            a POI card that rests at a short height (the collapsed/half snap for
            flora-fauna) shows its text/buttons at ~50 % opacity and reads as a
            washed grey card ("ausgegraut"), while it should be crisp like the
            other balloons (Alex 2026-07-10, 113358 vs 113359). */}
        {(snap === 'half' || snap === 'expanded' || height > collapsedHeight + 20) && halfContent && (
          <div style={{
            marginTop: 16,
            opacity: isDragging ? Math.min(1, (height - collapsedHeight - 10) / 60) : 1,
            transition: isDragging ? 'none' : 'opacity 0.2s',
          }}>
            {halfContent}
          </div>
        )}

        {/* Expanded content: full route intelligence */}
        {(snap === 'expanded' || height > window.innerHeight * 0.55) && expandedContent && (
          <div style={{
            marginTop: 16,
            opacity: Math.min(1, (height - window.innerHeight * 0.45) / 80),
            transition: isDragging ? 'none' : 'opacity 0.2s',
          }}>
            {expandedContent}
          </div>
        )}
      </div>
    </div>
  )
}
